/**
 * Form templates — upload the brokerage's real form PDFs and map their fields.
 *
 * Until now this was API-only: the six live PDFs were loaded by a script, and no
 * admin could add or fix one. This screen is that path.
 *
 * The mapping half is the part that matters, and it is not symmetric:
 *
 *   AUTO_FIELDS (a fillable PDF) — DocuSign turns each of the PDF's own named
 *     fields into a tab by itself. Mapping only says what to PREFILL into them,
 *     and whose signature line is whose (an unclaimed /Sig is stripped, or the
 *     client ends up signing the counterparty's line).
 *
 *   ANCHOR (a FLAT PDF — both Texas REALTORS agreements) — the PDF has no fields
 *     AT ALL. Every box the client can type in is a row created here: name the
 *     literal text to anchor to ("Client:"), which occurrence of it (the labels
 *     repeat for the broker and the co-signer), and how far the box sits from it.
 *     Map nothing and the agreement reaches the client with nowhere to write
 *     their name — which is exactly what happened live at Checkpoint #12.
 *
 * The server resolves the anchor against the real PDF and answers with the page
 * and x/y it landed on, so a wrong anchor is a 422 here rather than a blank form
 * in front of a buyer.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  formTemplateService,
  uploadTemplatePdf,
  type FieldMappingIn,
  type FormTemplate,
  type TemplateField,
} from "@re-platform/api-client";
import { Button, Card, Empty, StatusPill, Toast } from "@re-platform/ui";

import { api } from "../../lib/api";
import {
  bodyRow,
  tableShell,
  TableScroll,
  Th,
  theadRow,
} from "../../lib/tables";
import { useRoles } from "../../lib/roles";

/** Mirrors trec-svc's FORM_TYPES tuple (models/trec.py). */
const FORM_TYPES = [
  "buyer_representation_agreement",
  "exclusive_right_to_sell",
  "exclusive_right_to_lease",
  "iabs",
  "home_inspection_notice",
  "consumer_protection_notice",
  "sellers_disclosure",
  "dual_capacity_consent",
  "residential_contract_1_to_4_family",
  "buyer_representation",
  "lease_application",
  "tenant_application",
  "addendum",
  "amendment",
];

/** Mirrors form_template_fields.signer_role — CO_CLIENT since V056, where the
 *  spouse became a real second recipient on the envelope rather than a line to
 *  strip. Leaving it out of this list meant a staff member could not bind the
 *  co-signer's row by hand at all. */
const SIGNER_ROLES = [
  "CLIENT",
  "CO_CLIENT",
  "BUYER",
  "SELLER",
  "AGENT",
] as const;

export function FormTemplates() {
  const roles = useRoles();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ["form-templates"],
    queryFn: () => formTemplateService.list(api),
  });
  // The names the server can actually prefill. Fetched rather than hardcoded so
  // the picker can never offer one that resolves to nothing.
  const canonicalQuery = useQuery({
    queryKey: ["canonical-fields"],
    queryFn: () => formTemplateService.canonicalFields(api),
  });

  const templates = templatesQuery.data ?? [];
  const canonical = canonicalQuery.data;
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ["form-templates"] });

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Form templates</h1>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
        {/* A super_admin is editing the PLATFORM catalog, not a brokerage's own
            — what they upload here becomes the standard every brokerage starts
            from, so telling them their upload "overrides the platform form"
            describes the opposite of what is about to happen. */}
        {roles.isSuperAdmin ? (
          <>
            The platform-standard PDFs sent for signature — the baseline every
            brokerage starts from. A brokerage can upload its own to override
            one of these for its clients.
          </>
        ) : (
          <>
            The real PDFs sent for signature. A brokerage upload overrides the
            platform-standard form of the same type for your clients.
          </>
        )}
      </p>

      <UploadCard onUploaded={refresh} onToast={setToast} />

      {templatesQuery.isError && (
        <Card padding="lg" style={{ marginTop: 16 }}>
          <p style={{ margin: 0 }}>
            Couldn't load templates: {(templatesQuery.error as Error).message}.
          </p>
        </Card>
      )}

      {templatesQuery.isSuccess && templates.length === 0 && (
        <div style={{ marginTop: 24 }}>
          <Empty
            title="No templates yet"
            description="Upload a form PDF to start sending the real document."
          />
        </div>
      )}

      {templates.map((t) => (
        <TemplateCard
          key={t.id}
          template={t}
          canonicalFields={canonical}
          open={openId === t.id}
          onToggle={() => setOpenId(openId === t.id ? null : t.id)}
          onChanged={refresh}
          onToast={setToast}
        />
      ))}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </>
  );
}

function UploadCard({
  onUploaded,
  onToast,
}: {
  onUploaded: () => void;
  onToast: (msg: string) => void;
}) {
  const [formType, setFormType] = useState(FORM_TYPES[0]!);
  const [version, setVersion] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<FormTemplate | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a PDF first.");
      // Presign → PUT the bytes straight to S3 → register. The server then reads
      // the object back and decides AUTO_FIELDS vs ANCHOR from what's in it.
      const presigned = await formTemplateService.uploadUrl(api, {
        form_type: formType,
        template_version: version,
        filename: file.name,
      });
      await uploadTemplatePdf(presigned.upload_url, file);
      return formTemplateService.register(api, {
        form_type: formType,
        template_version: version,
        display_name: displayName || file.name,
        s3_key: presigned.s3_key,
      });
    },
    onSuccess: (template) => {
      setResult(template);
      setFile(null);
      onUploaded();
    },
    onError: (err: Error) => onToast(err.message),
  });

  const ready = Boolean(file && version.trim());

  return (
    <Card padding="md" style={{ marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-end",
        }}
      >
        <Field label="Form type">
          <select
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            style={inputStyle}
          >
            {FORM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Version">
          <input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="TXR 1501"
            style={inputStyle}
          />
        </Field>
        <Field label="Display name">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Buyer/Tenant Representation Agreement"
            style={{ ...inputStyle, minWidth: 280 }}
          />
        </Field>
        <Field label="PDF">
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={{ fontSize: 13 }}
          />
        </Field>
        <Button
          onClick={() => upload.mutate()}
          disabled={!ready || upload.isPending}
        >
          {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
      </div>

      {result && (
        <p style={{ marginBottom: 0, fontSize: 13 }}>
          Registered <b>{result.display_name}</b> — {result.page_count} pages,{" "}
          <StatusPill
            tone={result.tab_strategy === "ANCHOR" ? "warning" : "success"}
          >
            {result.tab_strategy}
          </StatusPill>{" "}
          {result.tab_strategy === "ANCHOR"
            ? "— this PDF is FLAT (no form fields). Nothing is typeable until you map its fields below."
            : `— ${result.field_count} fillable fields found.`}
        </p>
      )}
    </Card>
  );
}

function TemplateCard({
  template,
  canonicalFields,
  open,
  onToggle,
  onChanged,
  onToast,
}: {
  template: FormTemplate;
  canonicalFields: { party: string[]; document: string[] } | undefined;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onToast: (msg: string) => void;
}) {
  const isAnchor = template.tab_strategy === "ANCHOR";
  const signatures = template.fields.filter(
    (f) => f.field_kind === "signature",
  ).length;

  // V073 — a template lands as DRAFT and only goes live once its mapping
  // validates AND the agent has looked at a test-filled preview. Fetched while
  // the card is open so what's still missing shows WHILE they work.
  const isDraft = template.status === "DRAFT";
  const validation = useQuery({
    queryKey: ["template-validation", template.id],
    queryFn: () => formTemplateService.validation(api, template.id),
    enabled: open && isDraft,
    retry: false,
  });
  const [previewed, setPreviewed] = useState(false);

  const preview = useMutation({
    mutationFn: () => formTemplateService.preview(api, template.id),
    onSuccess: (data) => {
      setPreviewed(true);
      // Opened by navigation rather than fetch — no bucket CORS rule needed,
      // and the browser renders the PDF itself.
      window.open(data.preview_url, "_blank", "noopener");
    },
    onError: (err: Error) => onToast(err.message),
  });

  const activate = useMutation({
    mutationFn: () => formTemplateService.activate(api, template.id),
    onSuccess: () => {
      onToast("Template is live — it'll be used on your next send.");
      onChanged();
    },
    onError: (err: Error) => onToast(err.message),
  });

  const save = useMutation({
    mutationFn: (fields: FieldMappingIn[]) =>
      formTemplateService.updateFields(api, template.id, fields),
    onSuccess: (fields) => {
      const anchored = fields.filter((f) => f.anchor_string);
      onToast(
        anchored.length
          ? `Saved — ${anchored
              .map(
                (f) =>
                  `${f.pdf_field_name} → p${f.page_number} (${f.x_position},${f.y_position})`,
              )
              .join(", ")}`
          : "Saved.",
      );
      onChanged();
      // The mapping changed, so the verdict and the preview are both stale.
      void validation.refetch();
      setPreviewed(false);
    },
    onError: (err: Error) => onToast(err.message),
  });

  return (
    <Card padding="md" style={{ marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div>
          <b>{template.display_name}</b>{" "}
          <StatusPill tone={template.tenant_id ? "info" : "neutral"}>
            {template.tenant_id ? "Brokerage" : "Platform"}
          </StatusPill>{" "}
          <StatusPill tone={isAnchor ? "warning" : "success"}>
            {template.tab_strategy}
          </StatusPill>{" "}
          {isDraft && <StatusPill tone="warning">Draft — not sent</StatusPill>}
          {template.owner_agent_id && (
            <>
              {" "}
              <StatusPill tone="info">Yours</StatusPill>
            </>
          )}
          <div
            style={{
              color: "var(--rp-color-text-muted)",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            {template.form_type} · {template.template_version} ·{" "}
            {template.page_count ?? "?"} pages · {template.fields.length} mapped
            fields · {signatures} signature{signatures === 1 ? "" : "s"}
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={onToggle}>
          {open ? "Close" : "Map fields"}
        </Button>
      </div>

      {open && (
        <>
          {isAnchor && template.fields.length === 0 && (
            <p
              style={{
                fontSize: 13,
                color: "var(--rp-color-text-muted)",
                marginBottom: 0,
              }}
            >
              This PDF is flat — it has no fields of its own. Every box the
              client can type in has to be added below, or the form goes out
              blank and unfillable.
            </p>
          )}

          <FieldTable
            fields={template.fields}
            isAnchor={isAnchor}
            canonicalFields={canonicalFields}
            busy={save.isPending}
            onSave={(mapping) => save.mutate([mapping])}
          />

          {/* V073 — the DRAFT gate. Everything here exists because the failure
              mode is a legally-binding document with blanks where the
              commission should be. */}
          {isDraft && (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 8,
                background: "var(--rp-color-surface-2)",
              }}
            >
              <b style={{ fontSize: 14 }}>Before this can go live</b>

              {validation.data?.missing_required.length ? (
                <p style={{ fontSize: 13, margin: "8px 0 0" }}>
                  Not yet bound to anything:{" "}
                  <b>{validation.data.missing_required.join(", ")}</b>
                </p>
              ) : null}

              {validation.data?.problems.map((problem) => (
                <p
                  key={problem}
                  style={{
                    fontSize: 13,
                    margin: "8px 0 0",
                    color: "var(--rp-color-danger)",
                  }}
                >
                  {problem}
                </p>
              ))}

              {validation.data?.warnings.map((warning) => (
                <p
                  key={warning}
                  style={{
                    fontSize: 13,
                    margin: "8px 0 0",
                    color: "var(--rp-color-text-muted)",
                  }}
                >
                  {warning}
                </p>
              ))}

              {validation.data?.ok && !previewed && (
                <p style={{ fontSize: 13, margin: "8px 0 0" }}>
                  The mapping checks out. Now open a test-filled copy and check
                  the values landed in the right boxes — we can tell that a
                  field is bound to something, not that it's bound to the right
                  thing.
                </p>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={preview.isPending}
                  onClick={() => preview.mutate()}
                >
                  {preview.isPending
                    ? "Building…"
                    : previewed
                      ? "Open preview again"
                      : "Preview with sample data"}
                </Button>
                <Button
                  size="sm"
                  // Both gates, deliberately: the server refuses an invalid
                  // mapping anyway, but it CANNOT know whether a human looked
                  // at the preview — so that half is enforced here.
                  disabled={
                    !validation.data?.ok || !previewed || activate.isPending
                  }
                  title={
                    !validation.data?.ok
                      ? "Fix the problems above first"
                      : !previewed
                        ? "Open the preview and check it first"
                        : "Make this template live"
                  }
                  onClick={() => activate.mutate()}
                >
                  {activate.isPending ? "Publishing…" : "Publish"}
                </Button>
              </div>
            </div>
          )}

          {isAnchor && (
            <AddAnchorField
              canonicalFields={canonicalFields}
              busy={save.isPending}
              onAdd={(mapping) => save.mutate([mapping])}
            />
          )}
        </>
      )}
    </Card>
  );
}

function FieldTable({
  fields,
  isAnchor,
  canonicalFields,
  busy,
  onSave,
}: {
  fields: TemplateField[];
  isAnchor: boolean;
  canonicalFields: { party: string[]; document: string[] } | undefined;
  busy: boolean;
  onSave: (mapping: FieldMappingIn) => void;
}) {
  if (fields.length === 0) return null;
  return (
    <TableScroll style={{ marginTop: 12 }}>
      <table style={{ ...tableShell, fontSize: 13 }}>
        <thead>
          <tr style={theadRow}>
            <Th>Field</Th>
            <Th>Kind</Th>
            <Th>Prefill with</Th>
            <Th>Signer</Th>
            {isAnchor && <Th>Anchor</Th>}
            <Th>Placed at</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <FieldRow
              key={f.id}
              field={f}
              isAnchor={isAnchor}
              canonicalFields={canonicalFields}
              busy={busy}
              onSave={onSave}
            />
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function FieldRow({
  field,
  isAnchor,
  canonicalFields,
  busy,
  onSave,
}: {
  field: TemplateField;
  isAnchor: boolean;
  canonicalFields: { party: string[]; document: string[] } | undefined;
  busy: boolean;
  onSave: (mapping: FieldMappingIn) => void;
}) {
  const [canonical, setCanonical] = useState(field.canonical_field ?? "");
  const [signer, setSigner] = useState(field.signer_role ?? "");

  const dirty =
    canonical !== (field.canonical_field ?? "") ||
    signer !== (field.signer_role ?? "");

  return (
    <tr style={bodyRow}>
      <td style={cellStyle}>
        <code>{field.pdf_field_name}</code>
      </td>
      <td style={cellStyle}>{field.field_kind}</td>
      <td style={cellStyle}>
        <CanonicalPicker
          value={canonical}
          onChange={setCanonical}
          canonicalFields={canonicalFields}
        />
      </td>
      <td style={cellStyle}>
        <select
          value={signer}
          onChange={(e) => setSigner(e.target.value)}
          style={inputStyle}
        >
          <option value="">— unassigned —</option>
          {SIGNER_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      {isAnchor && (
        <td style={cellStyle}>
          <code>{field.anchor_string ?? "—"}</code>
          {field.anchor_string && ` #${field.anchor_occurrence}`}
        </td>
      )}
      <td style={cellStyle}>
        {field.x_position === null ? (
          <span style={{ color: "var(--rp-color-text-muted)" }}>—</span>
        ) : (
          <code>
            p{field.page_number} ({field.x_position},{field.y_position})
          </code>
        )}
      </td>
      <td style={cellStyle}>
        <Button
          size="sm"
          variant="secondary"
          disabled={!dirty || busy}
          onClick={() =>
            onSave({
              pdf_field_name: field.pdf_field_name,
              canonical_field: canonical || null,
              signer_role: (signer || null) as FieldMappingIn["signer_role"],
              // Re-send the anchor: the server re-resolves the position from it,
              // so a row saved without it would lose its placement.
              field_kind: field.field_kind,
              anchor_string: field.anchor_string,
              anchor_occurrence: field.anchor_occurrence,
              x_offset: field.x_offset,
              y_offset: field.y_offset,
            })
          }
        >
          Save
        </Button>
      </td>
    </tr>
  );
}

function AddAnchorField({
  canonicalFields,
  busy,
  onAdd,
}: {
  canonicalFields: { party: string[]; document: string[] } | undefined;
  busy: boolean;
  onAdd: (mapping: FieldMappingIn) => void;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"text" | "signature">("text");
  const [canonical, setCanonical] = useState("");
  const [anchor, setAnchor] = useState("");
  const [occurrence, setOccurrence] = useState(1);
  const [xOffset, setXOffset] = useState(40);
  const [yOffset, setYOffset] = useState(-11);

  const ready = name.trim() !== "" && anchor.trim() !== "";

  return (
    <div
      style={{
        marginTop: 16,
        paddingTop: 12,
        borderTop: "1px solid var(--rp-color-border)",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        Add a box
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "flex-end",
        }}
      >
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="client_name"
            style={inputStyle}
          />
        </Field>
        <Field label="Kind">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as "text" | "signature")}
            style={inputStyle}
          >
            <option value="text">text</option>
            <option value="signature">signature</option>
          </select>
        </Field>
        <Field label="Prefill with">
          <CanonicalPicker
            value={canonical}
            onChange={setCanonical}
            canonicalFields={canonicalFields}
          />
        </Field>
        <Field label="Anchor text (must be in the PDF)">
          <input
            value={anchor}
            onChange={(e) => setAnchor(e.target.value)}
            placeholder="Client:"
            style={{ ...inputStyle, minWidth: 200 }}
          />
        </Field>
        <Field label="Occurrence">
          <input
            type="number"
            min={1}
            value={occurrence}
            onChange={(e) => setOccurrence(Number(e.target.value))}
            style={{ ...inputStyle, width: 70 }}
          />
        </Field>
        <Field label="X offset">
          <input
            type="number"
            value={xOffset}
            onChange={(e) => setXOffset(Number(e.target.value))}
            style={{ ...inputStyle, width: 80 }}
          />
        </Field>
        <Field label="Y offset">
          <input
            type="number"
            value={yOffset}
            onChange={(e) => setYOffset(Number(e.target.value))}
            style={{ ...inputStyle, width: 80 }}
          />
        </Field>
        <Button
          disabled={!ready || busy}
          onClick={() =>
            onAdd({
              pdf_field_name: name.trim(),
              field_kind: kind,
              canonical_field: canonical || null,
              signer_role: kind === "signature" ? "CLIENT" : null,
              anchor_string: anchor.trim(),
              anchor_occurrence: occurrence,
              x_offset: xOffset,
              y_offset: yOffset,
            })
          }
        >
          {busy ? "Placing…" : "Place"}
        </Button>
      </div>
      <p
        style={{
          fontSize: 12,
          color: "var(--rp-color-text-muted)",
          marginBottom: 0,
        }}
      >
        The anchor is the form's <i>label</i> ("Client:"), so the offsets carry
        the box off it and onto the blank line — right for a label beside the
        line (x +40, y −11), up for one printed underneath it (x 0, y −16). The
        occurrence picks which copy of a repeated label you mean: on the TXR
        agreements #1 is the client's block and #2 the broker's.
      </p>
    </div>
  );
}

function CanonicalPicker({
  value,
  onChange,
  canonicalFields,
}: {
  value: string;
  onChange: (v: string) => void;
  canonicalFields: { party: string[]; document: string[] } | undefined;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    >
      <option value="">— leave blank —</option>
      <optgroup label="Parties">
        {(canonicalFields?.party ?? []).map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </optgroup>
      <optgroup label="Documents / property">
        {(canonicalFields?.document ?? []).map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: 12 }}>
      <span style={{ color: "var(--rp-color-text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "6px 8px",
  font: "inherit",
  fontSize: 13,
};

const cellStyle: React.CSSProperties = {
  padding: "8px 10px",
  verticalAlign: "middle",
  fontWeight: 400,
  textAlign: "left",
};
